import { useQuery } from "@tanstack/react-query";
import createPrivateAdminPanelInstance from "../../api/PrivateAdminPanelInstance";
import { API_ROUTES } from "../../api/ApiRoutes";
import { bonusAdminBody } from "./bonusAdminBody";
import type { RunOut, SeriesRunsPage } from "../../components/Promotions/DynamicBonuses/types";

type StoredToken = { AccessToken?: string };

async function fetchRunsPage(
  seriesId: string,
  pageKey?: string,
): Promise<SeriesRunsPage> {
  const tokenString = localStorage.getItem("token");
  const token: StoredToken | null = tokenString ? JSON.parse(tokenString) : null;
  const instance = createPrivateAdminPanelInstance(token?.AccessToken);
  const url = API_ROUTES.LIST_DYNAMIC_BONUS_SERIES_RUNS.replace("{series_id}", seriesId);
  const params = pageKey ? { page_key: pageKey } : undefined;
  const response = await instance.get(url, { params });
  return bonusAdminBody<SeriesRunsPage>(response) ?? { items: [], count: 0, next_page_key: null };
}

/** Walks every page_key until exhausted so the UI can show a single flat list. */
async function fetchAllRuns(seriesId: string): Promise<SeriesRunsPage> {
  const items: RunOut[] = [];
  let pageKey: string | undefined;
  let count = 0;

  do {
    const page = await fetchRunsPage(seriesId, pageKey);
    items.push(...(page.items ?? []));
    count = page.count ?? items.length;
    pageKey = page.next_page_key ?? undefined;
  } while (pageKey);

  return { items, count, next_page_key: null };
}

export const getSeriesRunsQueryKey = (seriesId: string) =>
  ["LIST_DYNAMIC_BONUS_SERIES_RUNS", seriesId, "all"] as const;

export const useListSeriesRuns = (seriesId: string) => {
  return useQuery<SeriesRunsPage>({
    queryKey: getSeriesRunsQueryKey(seriesId),
    queryFn: () => fetchAllRuns(seriesId),
    enabled: Boolean(seriesId),
    staleTime: 1000 * 30,
    refetchOnWindowFocus: false,
  });
};
